<template>
  <div class="tag-anchor" :style="styles" :id="$attrs._id">
        <div 
      class="tag-line"
      :style="lineStyle"
    ></div>
    <div 
      v-if="tailPosition !== 'none'"
      class="tag-dot"
      :style="{ backgroundColor: backgroundColour}"
    ></div>
    <div 
      class="tag-basic"
      :class="[
        { 'tag-basic--box-shadow' : boxShadow },
        { 'tag-basic--sold-out' : showSoldLabel },
      ]"
      ref="el"
      :style="labelStyle"
      v-html="label[activeLanguage.code]"
    ></div>
  </div>
</template>
<script>
export default {
    props: {
    'background-colour': {
      default: '#FFFFFF'
    },
    'box-shadow': {
      default: true
    },
    'colour': {
      default: '#676767'
    },
    'font-size': {
      default: 0.75
    },
    'label': {
      default: 'New Tag'
    },
    'padding': {
      default: () => [0.5, 1.1, 0.4, 1.1],
      type: Array
    },
    'rotation': {
      default: 0
    },
    'tail-height': {
      default: 4
    },
    'tail-position': {
      default: 'none'
    },
    'text-colour': {
      default: '#000000'
    },
    'pos': {
      default: () => ({
        x: 100,
        y: 100,
      }),
      type: Object
    },
    'show-sold-label': {
      default: false
    },
  },

  computed: {
    activeLanguage () {
      return this.$store.getters.language
    },

    styles () {
      return {
        left: `${(this.pos.x / 20)}em`,
        top: `${(this.pos.y / 20)}em`,
      }
    },

    labelStyle () {
      let style = {
        color: this.textColour,
        backgroundColor: this.backgroundColour,
        fontSize: this.fontSize + 'em',
        paddingTop: this.padding[0] + 'em', 
        paddingLeft: this.padding[3] + 'em', 
        paddingBottom: this.padding[2] + 'em', 
        paddingRight: this.padding[1] + 'em', 
      }

      switch (this.tailPosition) {
        case 'none':
          return style
        
        case 'bottom-left':
          return {
            ...style,
            bottom: 0,
            transform: `translate(-1em, -${this.tailHeight / this.fontSize}em)`,
          }

        case 'bottom-center':
          return {
            ...style,
            bottom: 0,
            left: '50%',
            transform: `translate(-50%, -${this.tailHeight / this.fontSize}em)`,
          }

        case 'bottom-right':
          return {
            ...style,
            bottom: 0,
            right: 0,
            transform: `translate(1em, -${this.tailHeight / this.fontSize}em)`,
          }

        case 'top-left':
          return {
            ...style,
            transform: `translate(-1em, ${this.tailHeight / this.fontSize}em)`,
          }

        case 'top-center':
          return {
            ...style,
            left: '50%',
            transform: `translate(-50%, ${this.tailHeight / this.fontSize}em)`,
          }

        case 'top-right':
          return {
            ...style,
            right: 0,
            transform: `translate(1em, ${this.tailHeight / this.fontSize}em)`,
          }

        case 'left-center':
          return {
            ...style,
            left: 0,
            top: '.125em',
            transform: `translate(${this.tailHeight / this.fontSize}em, -50%)`,
          }

        case 'right-center':
          return {
            ...style,
            right: 0,
            top: '.125em',
            transform: `translate(-${this.tailHeight / this.fontSize}em, -50%)`,
          }
      
        default:
          return style
      }
    },

    lineStyle () {
      let style = {
        backgroundColor: this.backgroundColour,
        display: this.tailPosition !== 'none' ? this.tailPosition : 'none',
        zIndex: 6,
      }

      switch (this.tailPosition) {        
        case 'bottom-left':
          return {
            ...style,
            height: `${this.tailHeight}em`,
            transform: `translateY(-${this.tailHeight}em)`
          }

        case 'bottom-center':
          return {
            ...style,
            height: `${this.tailHeight}em`,
            transform: `translateY(-${this.tailHeight}em)`
          }

        case 'bottom-right':
          return {
            ...style,
            height: `${this.tailHeight}em`,
            transform: `translateY(-${this.tailHeight}em)`
          }

        case 'top-left':
          return {
            ...style,
            bottom: 0,
            height: `${this.tailHeight}em`,
            transform: `translateY(${this.tailHeight}em)`
          }

        case 'top-center':
          return {
            ...style,
            bottom: 0,
            height: `${this.tailHeight}em`,
            transform: `translateY(${this.tailHeight}em)`
          }

        case 'top-right':
          return {
            ...style,
            bottom: 0,
            height: `${this.tailHeight}em`,
            transform: `translateY(${this.tailHeight}em)`
          }

        case 'left-center':
          return {
            ...style,
            height: `.25em`,
            right: `0`,
            top: `-0.125em`,
            width: `${this.tailHeight}em`,
            transform: `translateX(0)`
          }

        case 'right-center':
          return {
            ...style,
            height: `.25em`,
            left: `0`,
            top: `-0.125em`,
            width: `${this.tailHeight}em`,
            transform: `translateX(-${this.tailHeight}em)`
          }

        default:
          return ''
      }
    },
  }
}
</script>
<style lang="scss" scoped>
@import "@/styles/_variables.scss";
@import "@/styles/_mixins.scss";
@import "@/styles/_animations.scss";

.tag-anchor {
  color: $basic-tag-bg-color;
  font-size: 1em;
  position: absolute;
  pointer-events: none;
  transition: opacity 200ms ease-in-out, box-shadow 200ms ease-in-out;
  z-index: 15;

  &.inactive {
    opacity: .5 !important;
  }
}

.tag-dot {
  border-radius: 50%;
  bottom: -.25em;
  height: .5em;
  left: -.25em;
  pointer-events: auto;
  position: absolute;
  width: .5em;
}

.tag-line {
  left: -.125em;
  pointer-events: auto;
  position: absolute;
  width: .25em;
}

.tag-basic {
  border-radius: $basic-tag-border-radius;
  cursor: pointer;
  font-family: $touchscreen-font;
  font-weight: 400;
  letter-spacing: .1em;
  line-height: 1.1;
  width: max-content;
  pointer-events: auto;
  position: absolute;
  text-align: center;
  text-transform: uppercase;
  user-select: none;

  &--box-shadow {
    box-shadow: $tag-box-shadow;
  }

  &--sold-out {
    &::before {
      background: #e90303;
      border-radius: .4rem;
      color: #fff;
      content: 'sold out';
      font-size: .6rem;
      overflow: hidden;
      padding: .3rem .3rem .2rem;
      position: absolute;
      right: -2.5rem;
      transform: rotate(15deg) translate(-50%, -50%);
      transform-origin: left;
      top: 0;
      width: max-content;
    }
  }

  &--white {
    background-color: rgba($white, .8);
    color: $black;
  }

  &--orange {
    background-color: $basic-tag-orange-color;
    color: $black;
  }

  &--blue {
    background-color: $basic-tag-blue-color;
    color: $black;
  }

  &--black {
    color: $basic-tag-font-color;
  }

  &--text {
    background-color: transparent;
    box-shadow: none;
    color: inherit;
  }
}
</style>
